<template>
  <div class="player-list pretty-scroll">
    <div v-for="(player, index) in filteredPlayers" :key="player.id" :class="{'editing-score': player.id === editingPlayerId}" class="player">
      <div class="flex-row">
        <div class="player-tag">
          <slot :player="player" :score="getPlayerScore(player)" :avgAnswerSpeed="scoresByPlayer.find(score => score.participantId === player.id)?.avgAnswerSpeed" :tiebreakCount="scoresByPlayer.find(score => score.participantId === player.id)?.tiebreakCount" :index="index" name="player">
            <div v-if="showBusinessAvatars && player.avatar" class="avatar-svg-wrapper is-inline-icon">
              <span :style="{'mask-image': `url(${player.avatar})`}" class="avatar-svg-icon" />
            </div>
            <img v-else-if="showAvatar && player.avatar" :src="player.avatar">
            <p class="player-name">{{ player.name }}</p>
          </slot>
        </div>

        <div class="player-actions">
          <slot
            :score="getPlayerScore(player)"
            :player="player"
            :startEditScore="startEditScore"
            :isEditingScore="isEditingScore"
            name="actions"
          >
            <div v-if="!isEditingScore(player)">
              <div v-if="canRemovePlayer" @click="startRemovePlayer(player)" class="button is-light-bg">Remove</div>
              <div v-if="canEditScore" @click="startEditScore(player)" class="button is-light-bg">Edit Score</div>
            </div>
          </slot>
        </div>
      </div>
      <div v-if="isEditingScore(player)" class="edit-score">
        <b-field label="Score" style="flex-grow: 2;">
          <b-input v-model="newPlayerScore" type="number" placeholder="Enter new score" />
        </b-field>

        <div @click="editingPlayerId = null" class="button is-light-bg m-r-10">Cancel</div>
        <div @click="saveScore(player)" class="button is-light-bg">Save</div>
      </div>
    </div>
  </div>
</template>

<script>
import addUserResponse from '~/queries/addUserResponse'
import { getAllPlayerScores, removePlayerById } from '~/queries/playerQueries'
import AvatarControls from '~/components/mixins/AvatarControls'
import { avatarStyles } from '~/common/avatars'

export default {
  mixins: [ AvatarControls ],
  props: {
    players: {
      type: Array,
      required: true
    },
    eventId: {
      type: [String, Number],
      required: true
    },
    canEditScore: {
      type: Boolean,
      required: false,
      default: false
    },
    canRemovePlayer: {
      type: Boolean,
      required: false,
      default: false
    },
    orderedByScore: {
      type: Boolean,
      required: false,
      default: false
    },
    playerSearch: {
      type: String,
      required: false,
      default: ''
    }
  },
  data () {
    return {
      newPlayerScore: 0,
      newTiebreakScore: 0,
      editingPlayerId: null,
      scoresByPlayer: [],
      scoresSet: false,
      savingResponse: false
    }
  },
  computed: {
    filteredPlayers () {
      const filtered = this.players.filter((player) => {
        return player.name.toLowerCase().includes(this.playerSearch.toLowerCase())
      })

      if (this.orderedByScore) {
        return filtered.sort((a, b) => {
          return this.getPlayerScore(b) - this.getPlayerScore(a)
        })
      }

      return filtered
    },
    showBusinessAvatars () {
      return this.avatarStyle === avatarStyles.BUSINESS
    }
  },
  watch: {
    eventId: {
      handler () {
        if (this.eventId) {
          this.setScoresByPlayer()
        }
      },
      immediate: true
    }
  },
  methods: {
    saveScore (player) {
      const score = this.getPlayerScore(player) || 0
      const scoreDifference = this.newPlayerScore - score

      // const tiebreakScore = this.getTiebreakScore(player) || 0
      // const tiebreakDifference = this.newTiebreakScore - tiebreakScore
      // const tiebreakText = tiebreakDifference ? `(+${tiebreakScore} tiebreak)` : ''
      // const tiebreakTextTwo = tiebreakDifference ? `(+${this.newTiebreakScore} tiebreak)` : ''

      const message = `<p style="display: block; margin-top: -11px;">Do you want to change <b>${player.name}</b>'s score?</p>
                       <p><br><b>Old score:</b> ${score} points</p>
                       <p><b>New score:</b> ${this.newPlayerScore} points</p>`

      this.$buefy.dialog.confirm({
        title: 'Confirm score adjustment',
        message,
        confirmText: 'Change score',
        cancelText: 'Cancel',
        type: 'is-info',
        onConfirm: () => {
          this.startSaveManualScoreAdjustment(player, scoreDifference, 0)
        }
      })
    },
    async startSaveManualScoreAdjustment (player, scoreDifference, tiebreakDifference) {
      this.log('saving manual score adjustment with score difference: ', scoreDifference, ' and tiebreak difference: ', tiebreakDifference)
      if (scoreDifference && scoreDifference !== 0) {
        this.log('adjusting main score by: ', scoreDifference)
        await this.saveManualScoreAdjustment(player, scoreDifference)
      }

      // if (tiebreakDifference && tiebreakDifference !== 0) {
      //   this.log('adjusting tiebreak score by: ', tiebreakDifference)
      //   await this.saveManualScoreAdjustment(player, 0, true)
      // }

      this.$buefy.toast.open({
        message: 'Score adjustment saved',
        type: 'is-success'
      })
    },
    async saveManualScoreAdjustment (player, scoreDifference, isTiebreak = false) {
      this.savingResponse = true
      const uniquePosition = Date.now() % 100000000 // last 8 digits from current timestamp

      const variables = {
        event: this.eventId,
        participant: player.id,
        prompt: { text: 'Manual score adjustment' },
        response: { text: 'Manual score adjustment' },
        isCorrect: true,
        shouldScore: true,
        isTiebreak,
        answerScore: scoreDifference || 0,
        answerScoreMultiplier: 1,
        voteScoreMultiplier: 1,
        userResponseTime: 5000,
        slidePosition: uniquePosition
      }

      await this.$apollo.mutate({
        mutation: addUserResponse,
        variables
      })

      this.savingResponse = false
      this.$emit('scoreUpdated')

      this.editingPlayerId = null
      this.scoresSet = false
      await this.setScoresByPlayer()
    },
    async startEditScore (player) {
      this.editingPlayerId = player.id
      await this.setScoresByPlayer()
      this.newPlayerScore = this.getPlayerScore(player) || 0
      // this.newTiebreakScore = this.getTiebreakScore(player) || 0
    },
    cancelEditScore () {
      this.editingPlayerId = null
    },
    isEditingScore (player) {
      return player.id === this.editingPlayerId
    },
    getPlayerScore (player) {
      return this.scoresByPlayer.find(score => score.participantId === player.id)?.score
    },
    getTiebreakScore (player) {
      return this.scoresByPlayer.find(score => score.participantId === player.id)?.tiebreakCount
    },
    async setScoresByPlayer (forceUpdate = false) {
      if (this.scoresSet && !forceUpdate) { return }
      const { data } = await this.$apollo.query({
        query: getAllPlayerScores,
        variables: {
          eventId: this.eventId
        },
        fetchPolicy: 'no-cache'
      })

      this.log('fetching participant scores: ', data)
      const participantScores = data.participantScores || []

      if (participantScores?.length > 0) {
        // remove any scores where the player id is not found in participants array
        const scores = participantScores.filter((score) => {
          return this.players.find(player => player.id === score.participantId) !== undefined
        })
        this.scoresByPlayer = Object.freeze(scores)
        this.scoresSet = true
      }
    },
    startRemovePlayer (player) {
      const playerAvatar = player.avatar ? `<img src="${player.avatar}" width="17px" height="17px"> ` : ''
      this.$buefy.dialog.confirm({
        title: 'Are you sure?',
        message: `Do you want to remove player <b>${playerAvatar}${player.name}</b>? This action is not reversible.`,
        confirmText: `Remove "${player.name}"`,
        cancelText: 'Cancel',
        type: 'is-danger',
        onConfirm: () => {
          this.removePlayer(player)
        }
      })
    },
    async removePlayer (player) {
      await this.$apollo.mutate({
        mutation: removePlayerById,
        variables: {
          id: player.id
        }
      })

      this.$emit('playerRemoved', player.id)
      this.$emit('close')
    }
  }
}
</script>
<style lang="scss" scoped>
.avatar-svg-wrapper {
  &.is-inline-icon {
    width: 1.4em;
    height: 1.4em;
  }
  .avatar-svg-icon {
    background: white;
  }
  margin-right: 10px;
}

.pretty-scroll {
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 5px;
  }

  &::-webkit-scrollbar-track {
    width: 5px;
    margin:8px 10px 8px 0px;
  }

  &::-webkit-scrollbar-thumb {
    width: 5px;
    border-radius:5px;
    background-color: #ccc;
  }
}

.player-list {
  display: flex;
  flex-direction: column;
  height: 55vh;
  overflow-y: scroll;
  overflow-x: hidden;
  padding-right: .5em;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: .5em .3em .5em .5em;
}
.edit-score {
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin: 1dvi .4em .3em 0;

  .control {
    flex-grow: 1;
    margin-right: 1em;
  }

  ::v-deep .label {
    font-weight: normal;
    font-size: .7em;
    margin-bottom: .3em;
    opacity: .8;
  }

  .button {
    margin-top: .6em;
  }
}
.player {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  border-radius: 4px;
  padding: .3em .3em .3em .75em;
  margin-bottom: 2px;

  &:hover, &.editing-score {
    background-color: #f7f7f7;

    .button.is-light-bg {
      background-color: #d9eaf3;
    }
  }

  .flex-row {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .player-tag {
    display: flex;
    flex-direction: row;
    align-items: center;
    font-size: 1.2em;
  }

  img {
    width: 1.2em;
    height: 1.2em;
    display: inline-block;
    margin-right: .5em;
  }
}
.player-actions {
  display: flex;
  align-items: center;
  gap: 0 0.5em;
}

// imported: review
.player-answer-speed {
  display: block;
  font-size: .6em;
  font-style: italic;
  opacity: .65;
  margin-top: -.3em;
  letter-spacing: -.03em;

  span {
    transform: scaleX(.9);
    transform-origin: center left;
  }

  .icon {
    // display: none;
    width: 1.2em;
    padding-left: .1em;
  }
}
.place {
  margin-top: 2px;
  font-weight: bold;
  // font-size: 1em;
  font-size: 1rem;
  // text-transform: uppercase;
  opacity: 1;
  color: var(--contrast);
  // position: absolute;
  // left: 0; right: 0;
  // margin: auto;
}

.ghost-podium {
  position: absolute;
  display: flex;
  justify-content: start;
  align-items: end;
  padding-bottom: 0.65em;
}

.ranking {
  font-size: 0.9em;
  font-weight: bold;
  width: 40px;
  height: 40px;
  text-align: center;
  display: inline-block;
  padding: 0.2em 0.3em;
  border-radius: 250%;
  border: 1px solid #d1d1d1;
  margin-right: 0.5em;
}

.points {
  span {
    font-size: 1.2em;
  }
  small {
    font-size: 0.8em;
  }
}
</style>
